import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@familywallet/api-client';
import {
  type Account,
  type CreateDebtRequest,
  type Member,
  parseRateInput,
  sortCurrencies,
  toBaseAmount,
} from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type FieldError, type Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { FormError } from '../components/FormError';
import { Chip, Row } from '../components/FormRow';
import { PageHeader } from '../components/PageHeader';
import { PickerSheet, type PickerOption } from '../components/PickerSheet';
import { Button } from '../components/ui/button';
import { Card, ListGroup } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Segmented } from '../components/ui/segmented';
import { parsePositive } from '../lib/add-form';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { todayIn } from '../lib/dates';
import { type DebtFormValues, debtFormSchema } from '../lib/debt-form';
import { latestRateMap, useAccounts, useCurrentRates, usePeople } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey, useMembers } from '../lib/members';
import { formatAmount, groupAmountInput } from '../lib/money';

/** What Correct (or a person page) hands this screen to start from. */
export type DebtPrefill = Partial<DebtFormValues> & { personLabel?: string };

/** New debt: borrowed or lent, with an outside person or a family member, or an existing one. */
export function NewDebtPage() {
  const { t } = useTranslation();
  const me = useMe();
  const accounts = useAccounts();
  const rates = useCurrentRates();
  const members = useMembers();
  if (!me.data || !accounts.data || !rates.data || members.isPending) {
    if (accounts.isError || rates.isError) {
      return (
        <Card role="alert">
          <p>{t('common.loadFailed')}</p>
        </Card>
      );
    }
    return <p role="status">{t('app.loading')}</p>;
  }
  const everyone = (members.data?.pages.flatMap((p) => p.items) ?? []).filter(
    (m) => m.status === 'active',
  );
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('debts.newTitle')} back="/debts" />
      <DebtForm
        accounts={accounts.data.items.filter((a) => a.canRecord && a.archivedAt === null)}
        members={everyone}
        myId={me.data.member.id}
        myAllowedPrivate={me.data.permissions.includes('accounts.private')}
        baseCurrency={me.data.family.baseCurrency}
        currencies={sortCurrencies(me.data.family.currencies, me.data.family.baseCurrency)}
        timezone={me.data.family.timezone}
        latestRates={latestRateMap(rates.data)}
      />
    </div>
  );
}

function DebtForm({
  accounts,
  members,
  myId,
  myAllowedPrivate,
  baseCurrency,
  currencies,
  timezone,
  latestRates,
}: {
  accounts: Account[];
  members: Member[];
  myId: string;
  myAllowedPrivate: boolean;
  baseCurrency: string;
  currencies: string[];
  timezone: string;
  latestRates: Record<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const canOnBehalf = useCan('debts.on_behalf');
  const canOutside = useCan('debts.outside');
  const canManagePeople = useCan('people.manage');
  const canAddPeople = canOutside || canManagePeople;
  const today = todayIn(timezone);
  const prefill = ((location.state as { prefill?: DebtPrefill } | null)?.prefill ??
    {}) as DebtPrefill;
  const [personLabel, setPersonLabel] = useState(prefill.personLabel ?? params.get('name') ?? '');
  const [picker, setPicker] = useState<
    'person' | 'owner' | 'accountId' | 'counterpartAccountId' | null
  >(null);
  const [rateWanted, setRateWanted] = useState(false);

  const resolverRef = useRef<Resolver<DebtFormValues, unknown, CreateDebtRequest> | null>(null);
  const form = useForm<DebtFormValues, unknown, CreateDebtRequest>({
    resolver: (values, context, options) => resolverRef.current!(values, context, options),
    defaultValues: {
      direction: params.get('direction') === 'lent' ? 'lent' : 'borrowed',
      ownerMemberId: myId,
      personId: params.get('person') ?? '',
      personName: '',
      memberId: '',
      amount: '',
      currency: baseCurrency,
      existing: false,
      accountId: accounts.find((a) => a.ownerMemberId === myId)?.id ?? '',
      counterpartAccountId: '',
      occurredOn: today,
      note: '',
      isPrivate: false,
      rate: '',
      ...prefill,
    },
  });
  const v = useWatch({ control: form.control }) as DebtFormValues;
  const currency = v.currency;
  const foreign = currency !== baseCurrency;
  const askRate = foreign && (rateWanted || latestRates[currency] === undefined);
  const resolver = useMemo(() => zodResolver(debtFormSchema({ askRate })), [askRate]);
  useEffect(() => {
    resolverRef.current = resolver;
  }, [resolver]);

  const byId = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const holds = (a: Account) => a.balances.some((b) => b.currency === currency);
  const memberName = (id: string) => members.find((m) => m.id === id)?.displayName ?? '';

  const create = useMutation({
    mutationFn: (body: CreateDebtRequest) => api.debts.create(body, { idempotencyKey }),
    onSuccess: async (debt) => {
      renewKey();
      await invalidate();
      navigate(`/debts/${debt.id}`, { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'rate.required') setRateWanted(true);
    },
  });

  const errors = form.formState.errors;
  const errorText = (error: FieldError | undefined) =>
    error?.message ? t(error.message, { currency }) : null;
  const amount = parsePositive(v.amount ?? '', currency);
  const rate = askRate ? parseRateInput(v.rate ?? '') : (latestRates[currency] ?? null);
  const equivalent =
    foreign && amount && rate
      ? formatAmount(toBaseAmount(amount, rate), baseCurrency, i18n.language)
      : null;

  const accountOptions = (exclude?: string): PickerOption[] =>
    accounts
      .filter((a) => a.id !== exclude && holds(a))
      .map((a) => ({
        value: a.id,
        label: a.name,
        hint: [
          a.ownerMemberId === null ? t('accountDetail.shared') : (a.ownerDisplayName ?? ''),
          a.balances.map((b) => formatAmount(b.balance, b.currency, i18n.language)).join(' · '),
        ]
          .filter(Boolean)
          .join(' · '),
      }));

  const changeCurrency = (next: string) => {
    const typed = parsePositive(form.getValues('amount'), currency);
    form.setValue('currency', next);
    if (typed && parsePositive(typed, next)) form.setValue('amount', groupAmountInput(typed, next));
    const chosen = byId.get(form.getValues('accountId'));
    if (chosen && !chosen.balances.some((b) => b.currency === next)) form.setValue('accountId', '');
  };

  const ownerIsMe = v.ownerMemberId === myId;
  const accountLabel = v.direction === 'borrowed' ? t('debts.accountTo') : t('debts.accountFrom');

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) => create.mutate(body))}
    >
      <Segmented
        label={t('debts.direction')}
        options={[
          { value: 'borrowed' as const, label: t('debts.borrowed') },
          { value: 'lent' as const, label: t('debts.lent') },
        ]}
        value={v.direction}
        onChange={(d) => form.setValue('direction', d)}
      />

      <Card className="flex flex-col items-center gap-3">
        <label htmlFor="debt-amount" className="sr-only">
          {t('add.amount')}
        </label>
        <input
          id="debt-amount"
          inputMode={currency === 'LBP' ? 'text' : 'decimal'}
          autoComplete="off"
          dir="ltr"
          placeholder={currency === 'LBP' ? '500k' : '0.00'}
          aria-invalid={errors.amount ? true : undefined}
          className="w-full bg-transparent text-center text-[46px] leading-tight font-semibold tracking-[-1px] tabular-nums placeholder:text-muted focus-visible:outline-none"
          {...form.register('amount', {
            onBlur: () => {
              const parsed = parsePositive(form.getValues('amount'), currency);
              if (parsed) form.setValue('amount', groupAmountInput(parsed, currency));
            },
          })}
        />
        {currencies.length > 1 && (
          <div className="w-40">
            <Segmented
              label={t('add.currency')}
              options={currencies.map((c) => ({ value: c, label: c }))}
              value={currency}
              onChange={changeCurrency}
            />
          </div>
        )}
        <div className="flex flex-col items-center text-center" aria-live="polite">
          {equivalent && (
            <span dir="ltr" className="text-sm text-muted tabular-nums">
              ≈ {equivalent}
            </span>
          )}
          {currency === 'LBP' && !amount && (
            <span className="text-xs text-muted">{t('add.fastEntry')}</span>
          )}
          {errors.amount && <span className="text-sm text-danger">{errorText(errors.amount)}</span>}
        </div>
      </Card>

      <ListGroup>
        {canOnBehalf && (
          <Row label={t('debts.whose')}>
            <Chip onClick={() => setPicker('owner')}>
              {ownerIsMe ? t('debts.me') : memberName(v.ownerMemberId)}
            </Chip>
          </Row>
        )}
        <Row label={t('debts.person')} error={errorText(errors.personId)}>
          <Chip onClick={() => setPicker('person')}>{personLabel || t('debts.pickPerson')}</Chip>
        </Row>
        {!v.existing && (
          <Row label={accountLabel} error={errorText(errors.accountId)}>
            <Chip onClick={() => setPicker('accountId')}>
              {byId.get(v.accountId)?.name ?? t('add.pickAccount')}
            </Chip>
          </Row>
        )}
        {!v.existing && v.memberId && (
          <Row
            label={t('debts.theirAccount', { name: memberName(v.memberId) })}
            error={errorText(errors.counterpartAccountId)}
          >
            <Chip onClick={() => setPicker('counterpartAccountId')}>
              {byId.get(v.counterpartAccountId)?.name ?? t('add.pickAccount')}
            </Chip>
          </Row>
        )}
        <Row
          label={v.existing ? t('debts.startedOn') : t('add.date')}
          htmlFor="debt-date"
          error={errorText(errors.occurredOn)}
        >
          <input
            id="debt-date"
            type="date"
            max={today}
            className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
            {...form.register('occurredOn')}
          />
        </Row>
        <Row label={t('debts.note')} htmlFor="debt-note">
          <input
            id="debt-note"
            autoComplete="off"
            maxLength={500}
            placeholder={t('add.notePlaceholder')}
            className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] placeholder:text-muted focus-visible:outline-none"
            {...form.register('note')}
          />
        </Row>
      </ListGroup>

      <label className="flex min-h-11 items-start gap-3 px-1">
        <input
          type="checkbox"
          className="mt-1 size-5 accent-accent"
          {...form.register('existing')}
        />
        <span className="flex flex-col">
          <span className="font-medium">{t('debts.existing')}</span>
          <span className="text-sm text-muted">{t('debts.existingHint')}</span>
        </span>
      </label>
      {v.existing && ownerIsMe && myAllowedPrivate && (
        <label className="flex min-h-11 items-start gap-3 px-1">
          <input
            type="checkbox"
            className="mt-1 size-5 accent-accent"
            {...form.register('isPrivate')}
          />
          <span className="font-medium">{t('debts.private')}</span>
        </label>
      )}

      {askRate && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="debt-rate" className="text-[13px] font-medium text-muted">
            {t('rates.oneOffLabel', { base: baseCurrency, quote: currency })}
          </label>
          <Input
            id="debt-rate"
            inputMode="decimal"
            autoComplete="off"
            dir="ltr"
            placeholder="89,500"
            aria-invalid={errors.rate ? true : undefined}
            {...form.register('rate')}
          />
          {errors.rate && <p className="text-sm text-danger">{errorText(errors.rate)}</p>}
        </div>
      )}

      <FormError error={create.error} />
      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? t('common.saving') : t('debts.save')}
      </Button>

      {picker === 'person' && (
        <PersonPicker
          members={members.filter((m) => m.id !== v.ownerMemberId)}
          canAdd={canAddPeople}
          selected={v.personId || v.memberId}
          onPickPerson={(id, name) => {
            form.setValue('personId', id);
            form.setValue('personName', '');
            form.setValue('memberId', '');
            setPersonLabel(name);
            setPicker(null);
          }}
          onPickName={(name) => {
            form.setValue('personId', '');
            form.setValue('personName', name);
            form.setValue('memberId', '');
            setPersonLabel(name);
            setPicker(null);
          }}
          onPickMember={(m) => {
            form.setValue('personId', '');
            form.setValue('personName', '');
            form.setValue('memberId', m.id);
            setPersonLabel(m.displayName);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === 'owner' && (
        <PickerSheet
          title={t('debts.whose')}
          options={members.map((m) => ({
            value: m.id,
            label: m.id === myId ? t('debts.me') : m.displayName,
          }))}
          selected={v.ownerMemberId}
          onPick={(id) => {
            form.setValue('ownerMemberId', id);
            if (form.getValues('memberId') === id) {
              form.setValue('memberId', '');
              setPersonLabel('');
            }
            if (id !== myId) form.setValue('isPrivate', false);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {(picker === 'accountId' || picker === 'counterpartAccountId') && (
        <PickerSheet
          title={t('add.pickAccount')}
          options={accountOptions(picker === 'accountId' ? v.counterpartAccountId : v.accountId)}
          empty={t('add.noAccounts')}
          selected={v[picker]}
          onPick={(id) => {
            form.setValue(picker, id);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </form>
  );
}

/** People (most used first, search as you type), family members, and "Add …" for a new name. */
export function PersonPicker({
  members,
  canAdd,
  selected,
  onPickPerson,
  onPickName,
  onPickMember,
  onClose,
}: {
  members: Member[];
  canAdd: boolean;
  selected: string;
  onPickPerson: (id: string, name: string) => void;
  onPickName: (name: string) => void;
  onPickMember: (member: Member) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(timer);
  }, [q]);
  const people = usePeople(debounced);
  const needle = q.trim().toLowerCase();
  const found = people.data?.items ?? [];
  const matchingMembers = members.filter((m) => m.displayName.toLowerCase().includes(needle));
  const exact =
    found.some((p) => p.name.toLowerCase() === needle) ||
    matchingMembers.some((m) => m.displayName.toLowerCase() === needle);
  const options: PickerOption[] = [
    ...found.map((p) => ({ value: `p:${p.id}`, label: p.name, hint: p.phone ?? undefined })),
    ...matchingMembers.map((m) => ({
      value: `m:${m.id}`,
      label: m.displayName,
      hint: t('debts.familyMembers'),
    })),
  ];
  return (
    <PickerSheet
      title={t('debts.pickPerson')}
      search={{ value: q, onChange: setQ, label: t('debts.searchPeople') }}
      options={options}
      selected={
        selected
          ? members.some((m) => m.id === selected)
            ? `m:${selected}`
            : `p:${selected}`
          : undefined
      }
      onPick={(value) => {
        const id = value.slice(2);
        if (value.startsWith('m:')) onPickMember(members.find((m) => m.id === id)!);
        else onPickPerson(id, found.find((p) => p.id === id)?.name ?? '');
      }}
      onClose={onClose}
      empty={people.isPending ? t('app.loading') : undefined}
      footer={
        needle && !exact && canAdd ? (
          <Button variant="secondary" onClick={() => onPickName(q.trim())}>
            <Plus aria-hidden="true" className="size-5" strokeWidth={2} />
            {t('debts.newPerson', { name: q.trim() })}
          </Button>
        ) : null
      }
    />
  );
}
